import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import '../styles/SurveyPage.css';
import { getApiUrl } from '../config';
// Helper to get option text regardless of format
const getOptionText = (option) => {
    return typeof option === 'string' ? option : option.text;
};
const API_BASE_URL = getApiUrl();
const SurveyPage = ({ surveyUrl, surveyType, sessionId, eventSlug, joinCode, onComplete, }) => {
    const [surveyData, setSurveyData] = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {
        const fetchSurvey = async () => {
            try {
                setLoading(true);
                const response = await fetch(surveyUrl);
                if (!response.ok) {
                    throw new Error('Failed to load survey');
                }
                const data = await response.json();
                setSurveyData(data);
                setError(null);
            }
            catch (err) {
                setError('Unable to load survey. Please try again.');
                console.error('Error loading survey:', err);
            }
            finally {
                setLoading(false);
            }
        };
        fetchSurvey();
    }, [surveyUrl]);
    const handleOptionSelect = (key, option) => {
        setAnswers(prev => ({
            ...prev,
            [key]: option,
        }));
    };
    const handleTextChange = (key, value) => {
        setAnswers(prev => ({
            ...prev,
            [key]: value,
        }));
    };
    const getQuestionKey = (question, fallbackKey) => {
        return question.value || fallbackKey;
    };
    // Check if all questions are answered (including sub-questions in containers)
    const isComplete = surveyData
        ? surveyData.questions.every((q, qIndex) => {
            if (q.type === 'container') {
                return q.subQuestions.every((subQ, subIndex) => {
                    const fallbackKey = `${qIndex}-${subIndex}`;
                    const key = getQuestionKey(subQ, fallbackKey);
                    return answers[key] !== undefined && answers[key] !== '';
                });
            }
            const key = getQuestionKey(q, String(qIndex));
            return answers[key] !== undefined && answers[key] !== '';
        })
        : false;
    const handleSubmit = async () => {
        if (!isComplete || isSubmitting)
            return;
        if (!sessionId || !joinCode) {
            setSubmitError('Missing session data. Please rejoin the class.');
            return;
        }
        setSubmitError(null);
        setIsSubmitting(true);
        try {
            // Prefix all answer keys with "pre." or "post."
            const prefixedAnswers = Object.entries(answers).reduce((acc, [key, value]) => {
                acc[`${surveyType}.${key}`] = value;
                return acc;
            }, {});
            const response = await fetch(`${API_BASE_URL}/v1/surveyResponses`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId,
                    eventSlug,
                    joinCode,
                    answers: prefixedAnswers,
                }),
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data?.error?.message || 'Failed to submit survey');
            }
            onComplete();
        }
        catch (err) {
            console.error('Survey submission error:', err);
            setSubmitError(err.message || 'Unable to submit survey. Please try again.');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    if (loading) {
        return (_jsx("div", { className: "survey-page", children: _jsx("div", { className: "survey-loading", children: "Loading survey..." }) }));
    }
    if (error || !surveyData) {
        return (_jsx("div", { className: "survey-page", children: _jsx("div", { className: "survey-error", children: error || 'Survey not found' }) }));
    }
    return (_jsx("div", { className: "survey-page", children: _jsxs("div", { className: "survey-content", children: [_jsxs("div", { className: "survey-header", children: [_jsx("h1", { className: "survey-title", children: surveyData.title }), _jsx("p", { className: "survey-description", children: surveyData.description })] }), _jsxs("div", { className: "survey-questions-container", children: [surveyData.questions.map((question, qIndex) => (_jsxs("div", { className: "survey-question", children: [_jsxs("p", { className: "question-text", children: [qIndex + 1, ". ", question.question] }), question.type === 'multipleChoice' && (_jsxs(_Fragment, { children: ['image' in question && question.image && (_jsx("img", { src: question.image, alt: "", className: "question-image" })), _jsx("div", { className: "question-options", children: question.options.map((option, oIndex) => (_jsxs("label", { className: "option-label", children: [_jsx("input", { type: "radio", name: `question-${qIndex}`, value: option.text, checked: answers[getQuestionKey(question, String(qIndex))] === option.text, onChange: () => handleOptionSelect(getQuestionKey(question, String(qIndex)), option.text), className: "option-radio" }), _jsx("span", { className: "option-radio-custom" }), _jsx("span", { className: "option-text", children: option.text })] }, oIndex))) })] })), question.type === 'horizontalMultipleChoice' && (_jsxs(_Fragment, { children: ['image' in question && question.image && (_jsx("img", { src: question.image, alt: "", className: "question-image" })), _jsx("div", { className: "question-options-horizontal", children: question.options.map((option, oIndex) => {
                                                const optionText = getOptionText(option);
                                                return (_jsxs("label", { className: "option-label-horizontal", children: [_jsx("input", { type: "radio", name: `question-${qIndex}`, value: optionText, checked: answers[getQuestionKey(question, String(qIndex))] === optionText, onChange: () => handleOptionSelect(getQuestionKey(question, String(qIndex)), optionText), className: "option-radio" }), _jsx("span", { className: "option-radio-custom" }), _jsx("span", { className: "option-text-horizontal", children: optionText })] }, oIndex));
                                            }) })] })), question.type === 'text' && (_jsx("div", { className: "question-text-input", children: _jsx("textarea", { value: answers[getQuestionKey(question, String(qIndex))] || '', onChange: (e) => handleTextChange(getQuestionKey(question, String(qIndex)), e.target.value), placeholder: "Enter your answer...", className: "text-answer", rows: 3 }) })), question.type === 'container' && (_jsx("div", { className: "question-container", children: question.subQuestions.map((subQ, subIndex) => (_jsxs("div", { className: "sub-question", children: [_jsx("p", { className: "sub-question-text", children: subQ.question }), subQ.type === 'multipleChoice' && (_jsxs(_Fragment, { children: ['image' in subQ && subQ.image && (_jsx("img", { src: subQ.image, alt: "", className: "question-image" })), _jsx("div", { className: "question-options", children: subQ.options.map((option, oIndex) => (_jsxs("label", { className: "option-label", children: [_jsx("input", { type: "radio", name: `question-${qIndex}-${subIndex}`, value: option.text, checked: answers[getQuestionKey(subQ, `${qIndex}-${subIndex}`)] ===
                                                                        option.text, onChange: () => handleOptionSelect(getQuestionKey(subQ, `${qIndex}-${subIndex}`), option.text), className: "option-radio" }), _jsx("span", { className: "option-radio-custom" }), _jsx("span", { className: "option-text", children: option.text })] }, oIndex))) })] })), subQ.type === 'horizontalMultipleChoice' && (_jsxs(_Fragment, { children: ['image' in subQ && subQ.image && (_jsx("img", { src: subQ.image, alt: "", className: "question-image" })), _jsx("div", { className: "question-options-horizontal", children: subQ.options.map((option, oIndex) => {
                                                            const optionText = getOptionText(option);
                                                            return (_jsxs("label", { className: "option-label-horizontal", children: [_jsx("input", { type: "radio", name: `question-${qIndex}-${subIndex}`, value: optionText, checked: answers[getQuestionKey(subQ, `${qIndex}-${subIndex}`)] ===
                                                                            optionText, onChange: () => handleOptionSelect(getQuestionKey(subQ, `${qIndex}-${subIndex}`), optionText), className: "option-radio" }), _jsx("span", { className: "option-radio-custom" }), _jsx("span", { className: "option-text-horizontal", children: optionText })] }, oIndex));
                                                        }) })] })), subQ.type === 'text' && (_jsx("div", { className: "question-text-input", children: _jsx("textarea", { value: answers[getQuestionKey(subQ, `${qIndex}-${subIndex}`)] || '', onChange: (e) => handleTextChange(getQuestionKey(subQ, `${qIndex}-${subIndex}`), e.target.value), placeholder: "Enter your answer...", className: "text-answer", rows: 3 }) }))] }, subIndex))) }))] }, qIndex))), submitError && _jsx("div", { className: "survey-error", children: submitError }), _jsx("button", { className: `survey-submit-btn ${!isComplete || isSubmitting ? 'survey-submit-btn-disabled' : ''}`, onClick: handleSubmit, disabled: !isComplete || isSubmitting, children: isSubmitting ? 'Submitting...' : 'Submit' })] })] }) }));
};
export default SurveyPage;
